import { Stack, Typography } from '@mui/material';
import { Grid } from '@mui/material';
import { FC, useMemo } from 'react';
import { Calendar } from '@vy/ui-design-system';
import { vyDate } from '@vy/utils';
import { AnalyticMetricV2 } from '../AnalyticMetric/AnalyticMetricV2';
import { VyDateRangePicker, VyDateRangeValue } from '../../../../../components/new-design/date-picker';

const DISPLAY_DATE_FORMAT = 'MMM DD, YYYY';

type InvoicesBreakdownProps = {
  totalAmount: number;
  inReviewCount: number;
  inReviewAmount: number;
  inOverageCount: number;
  inOverageAmount: number;
  formatter: (value: number) => string;
  dateRange: VyDateRangeValue;
  onDateRangeChange: (range: VyDateRangeValue) => void;
  totalAmountDataVy: string;
  inReviewDataVy: string;
  inOverageDataVy: string;
};

export const InvoicesBreakdown: FC<InvoicesBreakdownProps> = ({
  totalAmount,
  inReviewCount,
  inReviewAmount,
  inOverageCount,
  inOverageAmount,
  formatter,
  dateRange,
  onDateRangeChange,
  totalAmountDataVy,
  inReviewDataVy,
  inOverageDataVy,
}) => {
  const displayValue = useMemo(() => {
    const startLabel = dateRange.start ? vyDate(dateRange.start).format(DISPLAY_DATE_FORMAT) : '';
    const isEndToday = dateRange.end && vyDate(dateRange.end).isSame(vyDate(), 'day');
    const endLabel = !dateRange.end || isEndToday ? 'Now' : vyDate(dateRange.end).format(DISPLAY_DATE_FORMAT);

    return startLabel ? `${startLabel} - ${endLabel}` : '';
  }, [dateRange]);

  return (
    <Stack sx={{
      gap: 2.5
    }}>
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          alignItems: "center"
        }}>
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 600,
            color: "#01060D"
          }}>
          Breakdown
        </Typography>
        <VyDateRangePicker
          value={dateRange}
          onChange={onDateRangeChange}
          label=""
          format="MMM DD, YYYY"
          slotProps={{
            textField: {
              size: 'small',
              fullWidth: false,
              slotProps: {
                input: {
                  startAdornment: (
                    <Calendar
                      width={16}
                      height={16}
                      viewBox="0 0 24 24"
                      color="#5F6978"
                      style={{ marginRight: 4 }}
                    />
                  ),
                  endAdornment: null,
                  sx: {
                    border: '1px solid #D6DBE7',
                    borderRadius: 1,
                  },
                },
                htmlInput: displayValue ? { value: displayValue } : undefined,
              },
              sx: {
                '.MuiInputBase-input': {
                  fontSize: '12px',
                },
              },
            },
          }}
        />
      </Stack>
      <Grid container spacing={2} columns={2}>
        <Grid size={1}>
          <AnalyticMetricV2
            titleText="Total Amount"
            value={totalAmount}
            formatter={formatter}
            valueFontSize={20}
            titleSx={{
              marginBottom: '6px',
            }}
            dataVy={totalAmountDataVy}
          />
        </Grid>
        <Grid size={1}>
          <AnalyticMetricV2
            titleText={`In review (${inReviewCount})`}
            value={inReviewAmount}
            formatter={formatter}
            valueFontSize={20}
            titleSx={{
              marginBottom: '6px',
            }}
            dataVy={inReviewDataVy}
          />
        </Grid>
        <Grid size={1}>
          <AnalyticMetricV2
            titleText={`In Overage (${inOverageCount})`}
            value={inOverageAmount}
            formatter={formatter}
            valueFontSize={20}
            titleSx={{
              marginBottom: '6px',
            }}
            error
            dataVy={inOverageDataVy}
          />
        </Grid>
      </Grid>
    </Stack>
  );
};
